/**
 * Cache en mémoire pour les données fréquemment accédées
 * P2 Performance: Réduction des requêtes API
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface CacheOptions {
  /** Durée de vie en millisecondes (défaut: 5 minutes) */
  ttl?: number;
  /** Durée de vie étendue pour les données stale (défaut: 30 minutes) */
  staleTtl?: number;
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
const DEFAULT_STALE_TTL = 30 * 60 * 1000; // 30 minutes

class MemoryCache {
  private cache = new Map<string, CacheEntry<unknown>>();
  private pendingRequests = new Map<string, Promise<unknown>>();

  /**
   * Récupère une valeur du cache
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      return null;
    }

    // Vérifier si l'entrée a expiré
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Récupère une valeur, même si elle est "stale" (périmée mais utilisable)
   * Utile pour afficher des données périmées pendant le rechargement
   */
  getStale<T>(key: string, staleTtl = DEFAULT_STALE_TTL): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      return null;
    }

    // Vérifier si même les données stale ont expiré
    const staleExpiresAt = entry.timestamp + staleTtl;
    if (Date.now() > staleExpiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Vérifie si les données sont fraîches (non périmées)
   */
  isFresh(key: string): boolean {
    const entry = this.cache.get(key);
    return entry ? Date.now() <= entry.expiresAt : false;
  }

  /**
   * Stocke une valeur dans le cache
   */
  set<T>(key: string, data: T, options: CacheOptions = {}): void {
    const ttl = options.ttl ?? DEFAULT_TTL;
    const now = Date.now();

    this.cache.set(key, {
      data,
      timestamp: now,
      expiresAt: now + ttl,
    });
  }

  /**
   * Supprime une entrée du cache
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalide toutes les entrées correspondant à un préfixe
   * Ex: invalidatePrefix('products:') supprime products:123, products:456, etc.
   */
  invalidatePrefix(prefix: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Vide tout le cache
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Récupère ou charge une valeur (pattern stale-while-revalidate)
   * - Si les données sont fraîches, les retourne directement
   * - Si les données sont périmées mais dans la période stale, les retourne et recharge en arrière-plan
   * - Si pas de données, charge et met en cache
   */
  async getOrLoad<T>(
    key: string,
    loader: () => Promise<T>,
    options: CacheOptions = {}
  ): Promise<T> {
    const ttl = options.ttl ?? DEFAULT_TTL;
    const staleTtl = options.staleTtl ?? DEFAULT_STALE_TTL;

    // Vérifier si les données sont fraîches
    const fresh = this.get<T>(key);
    if (fresh !== null) {
      return fresh;
    }

    // Vérifier si on a des données stale
    const stale = this.getStale<T>(key, staleTtl);

    // Éviter les requêtes multiples pour la même clé
    const pending = this.pendingRequests.get(key);
    if (pending) {
      return pending as Promise<T>;
    }

    // Charger les nouvelles données
    const loadPromise = (async () => {
      try {
        const data = await loader();
        this.set(key, data, { ttl });
        return data;
      } finally {
        this.pendingRequests.delete(key);
      }
    })();

    this.pendingRequests.set(key, loadPromise);

    // Si on a des données stale, les retourner immédiatement
    // et laisser le chargement se faire en arrière-plan
    if (stale !== null) {
      loadPromise.catch(() => {}); // Ignorer les erreurs en arrière-plan
      return stale;
    }

    return loadPromise;
  }

  /**
   * Retourne les statistiques du cache
   */
  getStats(): { entries: number; keys: string[] } {
    return {
      entries: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// Instance singleton du cache
export const cache = new MemoryCache();

// Clés de cache prédéfinies pour la cohérence
export const CacheKeys = {
  products: (restaurantId: string) => `products:${restaurantId}`,
  stocks: (restaurantId: string) => `stocks:${restaurantId}`,
  recipes: (restaurantId: string) => `recipes:${restaurantId}`,
  suppliers: (restaurantId: string) => `suppliers:${restaurantId}`,
  lots: (restaurantId: string) => `lots:${restaurantId}`,
  alerts: (restaurantId: string) => `alerts:${restaurantId}`,
  dashboardStats: (restaurantId: string) => `dashboard:${restaurantId}`,
} as const;

// TTL prédéfinis selon la fréquence de mise à jour
export const CacheTTL = {
  /** Données rarement modifiées (catégories, zones) - 15 min */
  STATIC: 15 * 60 * 1000,
  /** Données de référence (produits, fournisseurs) - 5 min */
  REFERENCE: 5 * 60 * 1000,
  /** Données dynamiques (stocks, alertes) - 2 min */
  DYNAMIC: 2 * 60 * 1000,
  /** Données temps réel (dashboard) - 1 min */
  REALTIME: 1 * 60 * 1000,
} as const;
